import React, { useState, useEffect } from 'react';
import { auth } from '../../firebase.js';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { getApiClient } from '../../api/apiClient';
import { 
  PlusIcon, 
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import ClientForm from '../clients/ClientForm.jsx';
import ClientStats from '../clients/ClientStats.jsx';
import ClientTable from '../clients/ClientTable.jsx';

const Clients = () => {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [cuiData, setCuiData] = useState(null);
  const [cuiLoading, setCuiLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    company: '',
    city: '',
    taxIdentifier: '',
    regCom: '',
    vatPayer: false,
    clientType: 'INDIVIDUAL',
    preferredContact: 'EMAIL'
  });

  // Fetch clients
  const fetchClients = async () => {
    try {
      setLoading(true);
      const client = getApiClient();
      const response = await client.get('/admin/customer/all');
      console.log('Fetched clients:', response.data);
      const transformedClients = response.data.map(customer => ({
        id: customer.customerId,
        name: customer.name,
        email: customer.contact.email,
        phone: customer.contact.phone,
        address: customer.businessData?.address || '',
        company: customer.customerSubtype === 'BUSINESS_ENTITY' ? customer.businessData?.name : '',
        customerSubtype: customer.customerSubtype,
        taxIdentifier: customer.businessData?.tax_identifier || '',
        regCom: customer.businessData?.reg_com || '',
        city: customer.businessData?.city || '',
        countryCode: customer.businessData?.country_code || 'RO',
        subdivisionCode: customer.businessData?.subdivision_code || '',
        vatPayer: customer.businessData?.vat_payer || false,
        createdAt: new Date().toISOString()
      }));

      setClients(transformedClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchCuiData = async () => {
    const cuiNumber = formData.taxIdentifier?.trim();
    if (!cuiNumber) {
      alert('Please enter a CUI number first');
      return;
    }

    if (!auth.currentUser) {
      alert('Please log in first');
      return;
    }

    try {
      setCuiLoading(true);
      const client = getApiClient();
      console.log('Fetching CUI data for:', cuiNumber);
      
      const response = await client.get(`/cui/customer?cui=${cuiNumber}`);
      
      setCuiData(response.data);
      console.log('CUI data fetched:', response.data);
    
      if (response.data) {
        console.log('CUI verification successful:', response.data);
        
        setFormData(prev => ({
          ...prev,
          company: response.data.name || prev.company,
          address: response.data.address || prev.address,
          city: response.data.city || prev.city,
          regCom: response.data.reg_com || prev.regCom,
          vatPayer: response.data.vat_payer || prev.vatPayer
        }));
        
        alert('CUI verified successfully! Form fields have been populated automatically.');
      }
    } catch (error) {
      console.error('Error fetching CUI data:', error);
      setCuiData(null);
      alert('CUI not found or invalid. Please verify the CUI number.');
    } finally {
      setCuiLoading(false);
    }
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.phone?.includes(searchTerm) ||
    client.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetFormData = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      company: '',
      city: '',
      taxIdentifier: '',
      regCom: '',
      vatPayer: false,
      clientType: 'INDIVIDUAL',
      preferredContact: 'EMAIL'
    });
    setCuiData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const client = getApiClient();
      
      if (!formData.name || !formData.email) {
        alert('Name and email are required');
        return;
      }

      if (formData.clientType === 'BUSINESS_ENTITY') {
        if (!formData.taxIdentifier) {
          alert('CUI is required for business entities');
          return;
        }
        
        if (!cuiData) {
          alert('Please verify the CUI before saving');
          return;
        }
      }

      let formattedPhone = formData.phone?.trim() || '';
      if (formattedPhone && !formattedPhone.startsWith('+')) {
        if (formattedPhone.startsWith('0')) {
          formattedPhone = '+4' + formattedPhone;
        } else {
          formattedPhone = '+40' + formattedPhone;
        }
      }

      const customerData = {
        name: formData.name.trim(),
        customerSubtype: formData.clientType,
        contact: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formattedPhone,
          preferredContact: formData.preferredContact
        },
        source: "ADMIN"
      };

      if (formData.clientType === 'BUSINESS_ENTITY' || formData.address || formData.city) {
        let subdivisionCode = cuiData?.subdivision_code || 'B';
        
        if (!cuiData?.subdivision_code) {
          const cityLower = formData.city?.toLowerCase().trim();
          if (cityLower?.includes('cluj')) {
            subdivisionCode = 'CJ';
          } else if (cityLower?.includes('timisoara') || cityLower?.includes('timişoara')) {
            subdivisionCode = 'TM';
          } else if (cityLower?.includes('constanta') || cityLower?.includes('constanţa')) {
            subdivisionCode = 'CT';
          } else if (cityLower?.includes('iasi') || cityLower?.includes('iaşi')) {
            subdivisionCode = 'IS';
          }
        }

        customerData.businessData = {
          name: formData.company?.trim() || formData.name.trim(),
          tax_identifier: formData.taxIdentifier?.trim() || '',
          reg_com: formData.regCom?.trim() || '',
          address: formData.address?.trim() || '',
          city: formData.city?.trim() || 'Bucharest',
          country_code: cuiData?.country_code || 'RO',
          subdivisionCode: subdivisionCode,
          vat_payer: formData.vatPayer || false
        };
      }

      console.log('Sending customer data:', customerData);

      if (editingClient) {
        alert('Edit functionality will be implemented when API provides update endpoint');
      } else {
        await client.post('/admin/customer/add', customerData);
        console.log('Client created successfully:', customerData);
        alert('Customer created successfully!');
      }

      resetFormData();
      setShowAddModal(false);
      setEditingClient(null);
      
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      
      let errorMessage = 'Error saving client: ';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage += error.response.data;
        } else if (error.response.data.message) {
          errorMessage += error.response.data.message;
        } else {
          errorMessage += JSON.stringify(error.response.data);
        }
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm(t('confirmDeleteClient'))) return;
    
    try {
      alert('Delete functionality will be implemented when API provides delete endpoint');
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      company: client.company || '',
      city: client.city || '',
      taxIdentifier: client.taxIdentifier || '',
      regCom: client.regCom || '',
      vatPayer: client.vatPayer || false,
      clientType: client.company ? 'BUSINESS_ENTITY' : 'INDIVIDUAL',
      preferredContact: 'EMAIL'
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingClient(null);
    resetFormData();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className="mx-auto px-4 py-6 sm:px-6 md:px-8">
      {/* Header with Add Client Button */}
     

      {/* Statistics Cards */}
      <ClientStats clients={clients} />
 <div className="sm:flex sm:items-center sm:justify-end mb-6">
        <div className="mt-4 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            {t('addClient')}
          </button>
        </div>
      </div>
      {/* Search */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder={t('searchClients')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Clients Table */}
      <ClientTable 
        clients={filteredClients}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Client Form Modal */}
      <ClientForm
        showModal={showAddModal}
        editingClient={editingClient}
        formData={formData}
        setFormData={setFormData}
        cuiData={cuiData}
        cuiLoading={cuiLoading}
        onFetchCui={fetchCuiData}
        onSubmit={handleSubmit}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default Clients;
